import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {Observable, of} from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap, filter } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { MenuApiService } from '../menu-api.service';
import { MenuStoreService } from '../menu-store.service';
import { SharedStoreService } from '@emm/shared/services/shared-store.service';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import { ItemsActions } from '@emm/menu/store/items/items.actions';
import { ItemDetailActions } from '@emm/menu/store/item-detail/item-detail.actions';
import {MenuParamsContext} from '@emm/core/models/menu-key.model';
import {Locale} from '@emm/core/models/locale.model';
import {Venue} from '@emm/core/models/venue.model';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Injectable({
    providedIn: 'root'
})
export class ItemDetailEffects {

    select$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.select),
            filter(({ dispatchReload }) => RA.isTrue(dispatchReload)),
            switchMap(({ item }) => of(ItemDetailActions.load({ path: item.tagPath })))
        )
    );

    // oldLoad$ = createEffect(() =>
    //     this.actions$.pipe(
    //         ofType(ItemDetailActions.load),
    //         withLatestFrom(
    //             this.menuStore.queryConsumers.menuParamsContext$,
    //             this.sharedStore.queryLocales.localeSelected$,
    //             this.menuStore.queryFeatureScope.selected$,
    //             this.menuStore.queryLocationSets.selectedVenue$
    //         ),
    //         switchMap(([{path}, {effectiveDate, menuKey}, {language}, featureScope, selectedVenue]) =>
    //             (selectedVenue && selectedVenue.name !== 'Base Location')
    //                 ? this.api.getMenuItemForVenue(menuKey.consumer, menuKey.location, selectedVenue.venueNumber, path, effectiveDate)
    //                     .pipe(
    //                         map((item) => ItemDetailActions.loadSuccess({item})),
    //                         catchError(({error}) => of(ItemDetailActions.loadFailure({error})))
    //                     )
    //                 : this.api.getMenuItemDetailByFeatureScope(featureScope, effectiveDate, menuKey, path, language).pipe(
    //                     map((item) => ItemDetailActions.loadSuccess({item})),
    //                     catchError(({error}) => of(ItemDetailActions.loadFailure({error})))
    //                 )),
    //     )
    // );

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.load),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.sharedStore.queryLocales.localeSelected$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryLocationSets.selectedVenue$
            ),
            switchMap(([path, menuParamsContext, localeSelected, featureScope, selectedVenue]) => {
                const effectiveDate = menuParamsContext?.effectiveDate;
                const menuKey = menuParamsContext?.menuKey;
                const language = localeSelected?.language || '';

                return (selectedVenue && selectedVenue.name !== 'Base Location') ?
                    this.api.getMenuItemForVenue(menuKey.consumer, menuKey.location, selectedVenue.venueNumber, path.path, effectiveDate)
                        .pipe(
                            map((item) => ItemDetailActions.loadSuccess({item})),
                            catchError(({error}) => of(ItemDetailActions.loadFailure({error})))
                        ) :
                    this.api.getMenuItemDetailByFeatureScope(featureScope, effectiveDate, menuKey, path.path, language)
                        .pipe(
                            map((item) => ItemDetailActions.loadSuccess({item})),
                            catchError(({error}) => of(ItemDetailActions.loadFailure({error})))
                        );
            }),
        )
    );

    updateMenuItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.updateMenuItem),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{item, updates, dispatchReload, forceOverrideInheritance, forceOverrideInheritanceMap},
                           {effectiveDate, menuKey}, featureScope]) =>
                forceOverrideInheritanceMap ?
                    this.api.updateMenuItem(featureScope, effectiveDate, menuKey,
                        item.jsonPath ? item.jsonPath : item.tagPath, updates, false, null,
                        false, forceOverrideInheritanceMap).pipe(
                        map((_) => ItemDetailActions.updateMenuItemSuccess({item, dispatchReload})),
                        catchError((error) => {
                            const errorMessage = error?.error?.error?.message || API_ERRORS.GenericErrorMessage;
                            this.notificationService.error(errorMessage);
                            return of(ItemDetailActions.loadFailure({error: error?.error ?? error}));
                        })
                    ) :
                    this.api.updateMenuItem(featureScope, effectiveDate, menuKey,
                        item.jsonPath ? item.jsonPath : item.tagPath, updates, false, null, forceOverrideInheritance,
                        forceOverrideInheritanceMap).pipe(
                        map((_) => ItemDetailActions.updateMenuItemSuccess({item, dispatchReload})),
                        catchError((error) => {
                            const errorMessage = error?.error?.error?.message || API_ERRORS.GenericErrorMessage;
                            this.notificationService.error(errorMessage);
                            return of(ItemDetailActions.loadFailure({error: error?.error ?? error}));
                        })
                    )
            )
        )
    );

    add$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.add),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{ parent, children }, { effectiveDate, menuKey }, featureScope]) =>
                this.api.addMenuItems(featureScope, effectiveDate, menuKey, parent, children).pipe(
                    map((_) => ItemDetailActions.addSuccess()),
                    catchError(({ error }) => of(ItemDetailActions.addFailure({ error })))
                )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.delete),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryItemDetail.selectedSubmenusDeleteCascade$,
            ),
            switchMap(([{ path, itemName, selectedItem }, { effectiveDate, menuKey }, featureScope]) => {
                    return this.api.deleteMenuItem(featureScope, effectiveDate, menuKey, path).pipe(
                        map((_) => ItemDetailActions.deleteSuccess({itemName, path: selectedItem.tagPath, dispatchReload: true})),
                        catchError(({error}) => of(ItemDetailActions.deleteFailure({error})))
                    )
                }
            )
        )
    );

    deleteWithoutReload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.deleteWithoutReload),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryItemDetail.selectedSubmenusDeleteCascade$,
            ),
            switchMap(([{ path, itemName }, { effectiveDate, menuKey }, featureScope]) => {
                    return this.api.deleteMenuItem(featureScope, effectiveDate, menuKey, path).pipe(
                        map((_) => ItemDetailActions.deleteWithoutReloadSuccess({itemName})),
                        catchError(({error}) => of(ItemDetailActions.deleteWithoutReloadFailure({error})))
                    )
                }
            )
        )
    );

    updateMenuItemSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.updateMenuItemSuccess),
            tap(({ item, dispatchReload }) => {
                if (item) {
                    try {
                        this.notificationService.success(`${item.name} was successfully updated`);
                    } catch {
                        this.notificationService.error(`There was an error trying to update ${item.name}. Please refresh the page.`);
                    }
                }
            }),
            filter(({ dispatchReload }) => dispatchReload === true),
            switchMap(({ item }) => of(ItemDetailActions.load({ path: item.tagPath })))
        )
    );

    updateItemLocaleSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.updateItemLocaleSuccess),
            filter(({ dispatchReload }) => RA.isTrue(dispatchReload)),
            switchMap(({ item }) => of(ItemDetailActions.load({ path: item.tagPath })))
        )
    );

    paste$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.paste),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{ fromPath, toPath }, { effectiveDate, menuKey }, featureScope]) =>
                this.api.pasteMenuItem(featureScope, effectiveDate, menuKey, fromPath, toPath).pipe(
                    map((_) => ItemDetailActions.pasteSuccess()),
                    catchError(({ error }) => of(ItemDetailActions.pasteFailure({ error })))
                )
            )
        )
    );

    pasteSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ItemDetailActions.pasteSuccess),
                tap((_) => {
                    this.notificationService.success('Pasted successfully');
                }),
            ),
        {
            dispatch: false,
        }
    );

    deleteSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ItemDetailActions.deleteSuccess),
                tap(({ itemName, path, dispatchReload }) => {
                    this.notificationService.success('Deleted : ' + itemName);
                }),
                filter(({ dispatchReload }) => RA.isTrue(dispatchReload)),
                switchMap(({ path }) => of(ItemDetailActions.load({ path }))),
            ),
    );

    deleteWithoutReloadSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ItemDetailActions.deleteWithoutReloadSuccess),
                tap(({ itemName }) => {
                    this.notificationService.success(`Deleted: ${itemName}`);
                })
            ),
        {
            dispatch: false,
        }
    );

    move$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.move),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{ fromPath, toPath, index }, { effectiveDate, menuKey }, featureScope]) =>
                this.api.moveMenuItem(featureScope, effectiveDate, menuKey, fromPath, toPath, index).pipe(
                    map((_) => ItemDetailActions.moveSuccess()),
                    catchError(({ error }) => of(ItemDetailActions.moveFailure({ error })))
                )
            )
        )
    );

    toggleDisabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.toggleDisabled),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{ path, updates, cascade, forceOverrideInheritance }, { effectiveDate, menuKey }, featureScope]) =>
                this.api.updateMenuItem(featureScope, effectiveDate, menuKey, path, updates, cascade, null,
                    forceOverrideInheritance).pipe(
                    map((_) => ItemDetailActions.toggleDisabledSuccess()),
                    catchError(({ error }) => of(ItemDetailActions.toggleDisabledFailure({ error })))
                )
            )
        )
    );

    toggleDisabledMenuWide$ = createEffect(() => // FIXME check if applicable for submenus
        this.actions$.pipe(
            ofType(ItemDetailActions.toggleDisabledMenuWide),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$
            ),
            switchMap(([{ path, updates, cascade, forceOverrideInheritance }, { effectiveDate, menuKey }, featureScope]) =>
                this.api.updateMenuItem(featureScope, effectiveDate, menuKey, path, updates, cascade, null,
                    forceOverrideInheritance).pipe(
                    map((_) => ItemDetailActions.toggleDisabledMenuWideSuccess()),
                    catchError(({ error }) => of(ItemDetailActions.toggleDisabledMenuWideFailure({ error })))
                )
            )
        )
    );

    autoReloadTriggers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ItemDetailActions.toggleDisabledSuccess,
                ItemDetailActions.toggleDisabledMenuWideSuccess
            ),
            withLatestFrom(this.menuStore.queryItemDetail.selected$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedItem: ItemDetail) => of(ItemDetailActions.load({ path: selectedItem.tagPath })))
        )
    );

    cascadeSubmenuSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.selectCascadeSubmenus),
            switchMap(() => of(ItemDetailActions.selectCascadeSubmenusSuccess()))
        )
    );

    cascadeSubmenuDeselect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.deselectCascadeSubmenus),
            switchMap(() => of(ItemDetailActions.deselectCascadeSubmenusSuccess()))
        )
    );

    resetCascadeSubmenus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.resetCascadeSubmenus),
            switchMap(() => of(ItemDetailActions.resetCascadeSubmenusSuccess()))
        )
    );

    pasteCascade$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.pasteCascade),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryItemDetail.selectedSubmenusPasteCascade$,
            ),
            switchMap(([{ submenuTag, parentTag, childTagPath, selectedItem, position }, { effectiveDate, menuKey },
                           featureScope, selectedCascadeSubmenus]) => {
                    return this.api.pasteMenuItemCascade(featureScope, effectiveDate, menuKey, submenuTag, selectedCascadeSubmenus,
                        parentTag, childTagPath, position).pipe(
                        map((_) => ItemDetailActions.pasteCascadeSuccess({
                            path: selectedItem.tagPath,
                            dispatchReload: true
                        })),
                        catchError(({error}) => of(ItemDetailActions.pasteCascadeFailure({error})))
                    )
                }
            )
        )
    );

    deleteCascade$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.deleteCascade),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryItemDetail.selectedSubmenusDeleteCascade$,
            ),
            switchMap(([{ path, selectedItem }, { effectiveDate, menuKey }, featureScope, selectedCascadeSubmenus]) => {
                    return this.api.deleteMenuItemCascade(featureScope, effectiveDate, menuKey, path, selectedCascadeSubmenus).pipe(
                        map((_) => ItemDetailActions.deleteCascadeSuccess({
                            path: selectedItem.tagPath,
                            dispatchReload: true
                        })),
                        catchError(({error}) => of(ItemDetailActions.deleteCascadeFailure({error})))
                    )
                }
            )
        )
    );

    updateCascade$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.updateCascade),
            withLatestFrom(
                this.menuStore.queryConsumers.menuParamsContext$,
                this.menuStore.queryFeatureScope.selected$,
                this.menuStore.queryItemDetail.selectedSubmenusUpdateCascade$
            ),
            switchMap(([{path, cascadePath, updates, forceOverrideInheritance, forceOverrideInheritanceMap},
                           {effectiveDate, menuKey}, featureScope, selectedCascadeSubmenus]) => {
                    return forceOverrideInheritanceMap ? this.api.updateMenuItem(featureScope, effectiveDate, menuKey,
                            cascadePath, updates, true, selectedCascadeSubmenus, forceOverrideInheritance,
                            forceOverrideInheritanceMap).pipe(
                            map((_) => ItemDetailActions.updateCascadeSuccess({path})),
                            catchError((error) => {
                                const errorMessage = error?.error?.error?.message || API_ERRORS.GenericErrorMessage;
                                this.notificationService.error(errorMessage);
                                return of(ItemDetailActions.loadFailure({error: error?.error}));
                            })
                        ) :
                        this.api.updateMenuItem(featureScope, effectiveDate, menuKey, cascadePath, updates, true,
                            selectedCascadeSubmenus, forceOverrideInheritance).pipe(
                            map((_) => ItemDetailActions.updateCascadeSuccess({path})),
                            catchError((error) => {
                                const errorMessage = error?.error?.error?.message || API_ERRORS.GenericErrorMessage;
                                this.notificationService.error(errorMessage);
                                return of(ItemDetailActions.loadFailure({error: error?.error ?? error}));
                            })
                        )
                }
            )
        )
    );

    pasteCascadeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.pasteCascadeSuccess),
            filter(({ dispatchReload }) => RA.isTrue(dispatchReload)),
            switchMap(({ path }) => of(ItemDetailActions.load({ path })))
        ),
        {
            dispatch: true,
        }
    );

    updateCascadeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.updateCascadeSuccess),
            withLatestFrom(this.menuStore.queryItemDetail.selected$),
            tap(([_, itemDetail]) =>
                this.notificationService.success('Saved Cascade: ' + itemDetail?.name)
            ),
            switchMap(([{ path }]) => of(ItemDetailActions.load({ path })))
        ),
        {
            dispatch: true,
        }
    );

    deleteCascadeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemDetailActions.deleteCascadeSuccess),
            filter(({ dispatchReload }) => RA.isTrue(dispatchReload)),
            switchMap(({ path }) => of(ItemDetailActions.load({ path })))
        ),
        {
            dispatch: true,
        }
    );

    constructor(
        private actions$: Actions,
        private api: MenuApiService,
        private sharedStore: SharedStoreService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService
    ) {}

}
